import React, { useMemo } from "react";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import PortfolioChart from "../PortfolioChart";

/**
 * Equity curve + benchmark overlay card.
 *
 * Owns the "vs benchmark" delta badge, the benchmark dropdown selector and
 * the show/hide toggle. The parent page passes data + handlers; this
 * component is otherwise self-contained.
 */
export default function EquityCurveCard({
  portfolio,
  history,
  benchmark,
  showBenchmark,
  setShowBenchmark,
  benchmarks,
  benchmarkSymbol,
  changeBenchmark,
}) {
  const benchmarkDelta = useMemo(() => {
    if (!portfolio || !benchmark || !benchmark.points || benchmark.points.length < 2) return null;
    if (!history || history.length < 2) return null;
    const startCash = portfolio.starting_cash;
    if (!startCash) return null;
    const benchLast = benchmark.points[benchmark.points.length - 1].value;
    const portfolioPct = ((portfolio.total_value - startCash) / startCash) * 100;
    const benchPct = ((benchLast - startCash) / startCash) * 100;
    return { delta: portfolioPct - benchPct, portfolioPct, benchPct };
  }, [portfolio, benchmark, history]);

  return (
    <section className="mb-10">
      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-5 md:p-6" data-testid="portfolio-history-card">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
              Portfolio value over time
            </div>
            <div className="font-heading text-xl tracking-tight text-[#2A2F2B] mt-0.5 flex items-baseline gap-3 flex-wrap">
              Your equity curve
              {benchmarkDelta && showBenchmark && (
                <span
                  data-testid="vs-benchmark-delta"
                  className="text-sm font-mono-ticker font-semibold px-2.5 py-0.5 rounded-full"
                  style={{
                    backgroundColor: benchmarkDelta.delta >= 0 ? "#5C8D7315" : "#C66B5915",
                    color: benchmarkDelta.delta >= 0 ? "#5C8D73" : "#C66B59",
                  }}
                  title={`Your portfolio: ${benchmarkDelta.portfolioPct >= 0 ? "+" : ""}${benchmarkDelta.portfolioPct.toFixed(2)}% · ${benchmarkSymbol}: ${benchmarkDelta.benchPct >= 0 ? "+" : ""}${benchmarkDelta.benchPct.toFixed(2)}%`}
                >
                  {benchmarkDelta.delta >= 0 ? "+" : ""}{benchmarkDelta.delta.toFixed(2)}% vs {benchmarkSymbol}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {history.length >= 2 && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="benchmark-select"
                      className="text-xs rounded-full px-3 py-1.5 border border-[#EAE6E1] bg-white hover:border-[#547A8F] transition-soft flex items-center gap-1.5 text-[#2A2F2B]"
                    >
                      {benchmarks.find((b) => b.symbol === benchmarkSymbol)?.label || benchmarkSymbol}
                      <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.75} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-xl">
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#6B7068]">
                      Compare against
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {benchmarks.map((b) => (
                      <DropdownMenuItem
                        key={b.symbol}
                        data-testid={`benchmark-option-${b.symbol}`}
                        onClick={() => changeBenchmark(b.symbol)}
                        className="rounded-lg cursor-pointer"
                      >
                        <span className="font-mono-ticker font-semibold mr-2">{b.symbol}</span>
                        <span className="text-xs text-[#6B7068]">{b.label}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                {benchmark && benchmark.points && benchmark.points.length > 0 && (
                  <button
                    onClick={() => setShowBenchmark((v) => !v)}
                    data-testid="toggle-benchmark"
                    className={`text-xs rounded-full px-3 py-1.5 border transition-soft flex items-center gap-1.5 font-medium ${
                      showBenchmark
                        ? "bg-[#547A8F] text-white border-[#547A8F]"
                        : "bg-white text-[#6B7068] border-[#EAE6E1] hover:border-[#547A8F]"
                    }`}
                  >
                    <span className={`w-2.5 h-0.5 ${showBenchmark ? "bg-white" : "bg-[#547A8F]"}`} />
                    {showBenchmark ? "Hide" : "Show"}
                  </button>
                )}
              </>
            )}
            <span className="text-xs font-mono-ticker text-[#6B7068]">
              {history.length} {history.length === 1 ? "point" : "points"}
            </span>
          </div>
        </div>
        {history.length < 2 ? (
          <div className="text-sm text-[#6B7068] py-8 text-center" data-testid="history-empty">
            Your equity curve will appear here as you make trades over time.
            It auto-saves on every trade and daily visit.
          </div>
        ) : (
          <>
            <PortfolioChart
              snapshots={history}
              benchmark={showBenchmark ? benchmark : null}
              trendUp={portfolio?.total_pl >= 0}
              height={240}
            />
            <div className="flex items-center gap-4 mt-3 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6B7068]" data-testid="chart-legend">
              <span className="flex items-center gap-1.5">
                <span
                  className="w-3 h-0.5"
                  style={{ backgroundColor: portfolio?.total_pl >= 0 ? "#5C8D73" : "#C66B59" }}
                />
                Your portfolio
              </span>
              {showBenchmark && benchmark && benchmark.points && benchmark.points.length > 0 && (
                <span className="flex items-center gap-1.5">
                  <svg width="14" height="2" className="overflow-visible">
                    <line x1="0" y1="1" x2="14" y2="1" stroke="#547A8F" strokeWidth="1.5" strokeDasharray="3 2" />
                  </svg>
                  {benchmark.symbol} (rebased)
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
